import * as D from '../deps';

export { StatsSourceComponents } from '../externals';
export { DetectorActions } from './Detector';

export const DOMAIN_NAME = 'stats' as const;

export const StatsEvents = Object.freeze({
  VALUE_EXCHANGE: 'value_exchange',
  FLEX_T1_REWARD_EXPIRATION: 'flex_t1_reward_expiration',
} as const);

export const RewardsExpirationTriggers = Object.freeze({
  TIME: 'time',
  NO_CREDITS: 'NO_CREDITS',
} as const);

/**
 * These actions correspond to the exact string expected as input to hive tables,
 * which may differ from the actions defined in the client.
 */
export const StatsActions = Object.freeze({
  VALUE_EXCHANGE_END: 'value_exchange_end',

  ACCESS: 'access',
  SEARCH_ACTION_AUTOCOMPLETE: 'autocomplete',
  SEARCH_ACTION_CLEAR: 'clear',
  SEARCH_ACTION_EXIT: 'exit_navigate_away',
  SEARCH_ACTION_FULL_SEARCH: 'full_search',
  SEARCH_ACTION_SCROLL: 'scroll',
  SEARCH_ACTION_SEARCH: 'search',
  SEARCH_ACTION_SEARCH_FOR: 'search_for',
  SEARCH_ACTION_SELECT: 'select',
  SEARCH_FILTER_CHANGED: 'change_filter',

  DISABLED_REPLAY_TAPPED: 'disabled_replay_tapped',
  REPLAY_TAPPED: 'replay_tapped',
  SKIP_TAPPED: 'skip_tapped',

  // used in the action column of event_playback_interactions
  APS_ACTION_ERROR: 'aps_action_error',
  APS_EVENT_ERROR: 'aps_event_error',
  APS_PLAYBACK_ERROR: 'aps_playback_error',
  FAST_FORWARD_SEEK: 'ff15',
  PAUSE: 'pause',
  PLAY: 'play',
  REPEAT_DISABLED: 'repeat_disabled',
  REPEAT_SOURCE: 'repeat_source',
  REPEAT_TRACK: 'repeat_track',
  REWIND_SEEK: 'rew15',
  SCRUB: 'scrub_audio',
  SHUFFLE_OFF: 'shuffle_off',
  SHUFFLE_ON: 'shuffle_on',
  SIM_STREAM_PAUSE: 'sim_stream_pause',
  SIM_STREAM_PLAY: 'sim_stream_play',
  SKIP_BACK: 'skip_back',
  SKIP_FORWARD: 'skip_forward',

  CHRONOS_LIFECYCLE: 'chronos_lifecycle',
  USER_ACTION_SWITCH_TO_CLASSIC: 'switch_to_classic',

  AUDIOPLAYER_TIME_TO_AUDIO: 'audioplayer_time_to_audio',
  AUDIOPLAYER_TIME_TO_AUDIO_PRELOADED: 'audioplayer_time_to_audio_preloaded',

  // used in the event_type column of event_live_tracking
  LIVE_START: 'start',

  HOME_NAVIGATION: 'home_navigation',
  VIEW: 'view',
  WEB_CONCERT_CLICKED: 'clicked',
  WEB_CONCERT_SENT: 'sent',
  WEB_CONCERT_VIEWED: 'viewed',

  // unfortunately depending on which table it gets logged to, it uses either 'routing' or 'route' so we need definitions for both
  ROUTE: 'route',
  ROUTING: 'routing',

  // used in the action column of event_recently_played_carousel, and event_backstage
  ADD_TO_PLAYLIST: 'add_to_playlist',
  COLLECT: 'collect',
  DELETE_PLAYLIST: 'delete_playlist',
  DELETE_STATION: 'delete_station',
  NEW_PLAYLIST: 'new_playlist',
  OPEN: 'open',
  REMOVE_FROM_PLAYLIST: 'remove_from_playlist',
  SHARE: 'share',
  START_STATION_ARTIST: 'start_station_artist',
  START_STATION_TRACK: 'start_station_track',
  TIRED_TRACK: 'tired_track',
  UNCOLLECT: 'uncollect',
  WHY_SONG: 'why_song',

  // used in event_backstage
  // used when clicking on icons that open sourcecards
  INSUFFICIENT_RIGHTS_TOAST: 'insufficient_rights_toast', // used when clicking on disabled buttons that result in showing the insufficient rights toast
  OVERFLOW: 'overflow', // ellipsis trigger
  START_STATION: 'start_station', // station trigger
  START_STATION_TOAST: 'start_station_toast', // album bsp value when trying to start a station from album bsp

  // playlist bsp values
  ADD_SONGS: 'add_songs',
  EDIT: 'edit',
  SEARCH_SONG: 'search_song',
  SHUFFLE_PLAY: 'shuffle_play',

  TTM_CANCEL: 'TTM_CANCEL',
  TTM_CLEAR: 'TTM_CLEAR',
  TTM_START: 'TTM_START',
  TTM_TRANSITION: 'TTM_TRANSITION',

  BUFFERING_START: 'BUFFERING_START',
  BUFFERING_STOP: 'BUFFERING_STOP',

  CONTACT_SUPPORT_CLICKED: 'contact_support_clicked',
  NOT_YOU_CLICKED: 'not_you_clicked',
  REDEEM_ATTEMPT: 'redeem_attempt',

  // Browse
  DIRECTORY: 'directory',
  ROUTE_TO_BACKSTAGE: 'route_to_backstage',
  VIEW_ALL: 'view_all',

  // Collected Station
  AAM_TOGGLE_OFF: 'toggle_off',
  AAM_TOGGLE_ON: 'toggle_on',
  ADD_VARIETY: 'add_variety',
  EDIT_DESCRIPTION: 'edit_description',
  SEE_ALL: 'see_all',
  SKIP_TO_THUMBS: 'skip_to_thumbs',

  UPDATE_PREVIOUS_PLAYED_HARNESS: 'update_previous_played_harness',
} as const);

export const StatsSources = Object.freeze({
  BROWSE_GENRE: 'browse_genre',
  MY_STATIONS_EXISTING: 'my_stations_existing',
  MY_STATIONS_RECOMMENDED: 'my_stations_recommended',
  STATION_LIST_TOP: 'station_list_top',

  NOW_PLAYING: 'now_playing',
  SESSION_HISTORY: 'session_history',

  WEB_PLAYBACK_CONTROLS: 'web_playback_controls',

  BACKSTAGE: 'backstage',
  BACKSTAGE_ARTIST: 'backstage_artist',
  BACKSTAGE_GENRE: 'backstage_genre',
  BACKSTAGE_STATION: 'backstage_station',
  BACKSTAGE_TRACK: 'backstage_track',

  ATT: 'ATT',
  DEEP_LINK: 'deep_link',
  JS_API: 'js_api',
  OTHER: 'other',
  PROFILE_PAGE: 'profile_page',
  REC: 'rec',
  REC_STATION_LIST: 'rec_station_list',
  SEARCH: 'search',
  SMART_URL: 'smart_url',
  UNSPECIFIED: 'unspecified',
} as const);

export const StatsComponents = Object.freeze({
  ARTIST_GRID_ITEM: 'ArtistGridItem',
  ARTIST_LIST_ITEM: 'ArtistListItem',
  BACKSTAGE_ALBUM: 'BackstageAlbum',
  BACKSTAGE_ARTIST: 'BackstageArtist',
  BACKSTAGE_GENRE: 'BackstageGenre',
  BACKSTAGE_STATION: 'BackstageStation',
  BACKSTAGE_TRACK: 'BackstageTrack',
  EXTERNAL_API: 'ExternalApi', // not actually a component, but 'cleanest' way to track JS API items for stats the way we do others
  GENRE_GRID_ITEM: 'GenreGridItem',
  GENRE_LIST_ITEM: 'GenreListItem',
  LISTENERS_GRID_ITEM: 'ListenersGridItem',
  MEDIA_LIST_ITEM: 'MediaListItem',
  MY_MUSIC: 'MyMusic',
  MY_STATIONS: 'MyStations',
  MY_STATIONS_GRID_ITEM: 'MyStationsGridItem',
  MY_STATIONS_LIST_ITEM: 'MyStationsListItem',
  ONBOARDING: 'Onboarding',
  RECENTLY_PLAYED_CAROUSEL: 'RecentlyPlayedCarousel',
  SEARCH: 'Search',
  SEARCH_FIELD: 'SearchField',
  SEARCH_RESULTS: 'SearchResults',
  STATION_LIST_HANDLER: 'StationListHandler',
  STATIONS_YOU_MAY_LIKE: 'StationsYouMayLike',
  THUMBS_UP_LIST_ITEM: 'ThumbsUpListItem',
} as const);

export const WebRegistrationEvents = Object.freeze({
  // https://jira.savagebeast.com/browse/WEB-8283
  ANON_STATION_SELECTED_FROM_SEARCH: 'anon_station_selected_from_search',
  REGISTRATION_FAILED_EXISTING_EMAIL: 'registration_failed_existing_email',
  REGISTRATION_FAILED_OTHER: 'registration_failed_other',
  REGISTRATION_FAILED_UNDER_AGE: 'registration_failed_under_age',
  REGISTRATION_SUCCESSFUL: 'registration_successful',
  SIGN_UP_ATTEMPTED: 'sign_up_attempted',
  SIGN_UP_CLICKED_FROM_ANON_LISTENING: 'sign_up_clicked_from_anon_listening',
  TAP_TO_CREATE_STATION: 'tap_to_create_station',

  // TODO for prerequisite work: add it for Browse. Currently only for Browse Genres
  ANON_BROWSE_CLICKED: 'anon_browse_clicked',
  ANON_STATION_STARTED_FROM_BROWSE: 'anon_station_started_from_browse',
} as const);

export const AuthEvents = Object.freeze({
  LOGIN_SUCCESS: 'login_success',
} as const);

export const ListenerTypes = Object.freeze({
  AUTHENTICATED: 'authenticated',
  UNAUTHENTICATED: 'unauthenticated',
  ANON: 'anon',
} as const);

export const FeatureCodes = Object.freeze({
  PANDORA_PREMIUM: 'PANDORA_PREMIUM',
  NONE: 'NONE',
} as const);

export const ProductTypes = Object.freeze({
  SUBSCRIPTION: 'SUBSCRIPTION',
} as const);

export const SkuStores = Object.freeze({
  BANGOATT: 'BANGOATT',
} as const);

export const StatsStoreKeys = Object.freeze({
  BROWSE_SESSION_ID: 'browseSessionId',
  EVENTS: 'events',
  INITIALIZING: 'initializing',
  LIVE_TRACKING: 'liveTracking',
  PERFORMANCE: 'performance',
  PREVIOUS_PLAYED_HARNESS: 'previousPlayedHarness',
  SEARCH: 'search',
  SKIP_TYPE: 'skipType',
  TTM_EVENTS: 'timeToMusicEvents',
} as const);

export const Events = Object.freeze({
  AB_AUDIENCE_LOG: 'ab_audience_log',
  AB_EXPOSURE: 'ab_exposure',
  ADD_AUTOPLAY_FEEDBACK: 'add_autoplay_feedback',
  ADD_FEEDBACK: 'add_feedback',
  ADD_VARIETY: 'add_variety',
  APP_TIMINGS: 'app_timings',
  AUDIO_PLAYBACK_LIFECYCLE: 'audio_playback_lifecycle',
  AUDIO_TRACK_PLAYBACK: 'audio_track_playback_event',
  CHANGE_STATION: 'change_station',
  CLICKED_LTUX_PA_UPSELL_COACHMARK: 'clicked_ltux_pa_upsell_coachmark',
  CLICKED_START_TRIAL_PA_UPSELL_COACHMARK: 'clicked_start_trial_pa_upsell_coachmark',
  COACHMARK: 'coachmark',
  CREATE_STATION: 'create_station_web',
  DELETE_FEEDBACK: 'delete_feedback',
  DELETE_STATION: 'delete_station_web',
  DISCOVERY_TUNER_SCROLL: 'discovery_tuner_scroll',
  EVENT_BACKSTAGE: 'event_backstage',
  EVENT_IMAGE_FETCH_ERROR: 'event_image_fetch_error',
  EVENT_LISTENER_BUFFERING: 'event_listener_buffering',
  EVENT_LISTENER_IDLE: 'listener_idle',
  EVENT_PARTNER_LINK_ACTIONS: 'partner_link_actions',
  EVENT_PLAYLIST: 'playlist',
  EVENT_RECENTLY_PLAYED_CAROUSEL: 'event_recently_played_carousel',
  EVENT_SHARE: 'event_share',
  EVENT_WEB_AUTOMATIC_PLAYBACK: 'event_web_automatic_playback',
  EVENT_WEB_VIEW_MODE: 'event_web_view_mode',
  FLEX_ENGAGEMENT: 'flex_engagement',
  LIVE_TRACKING: 'live_tracking',
  LOG_DISCOVERY_TUNER_ACTION: 'discovery_tuner_selection',
  MEASURE_FRAMES: 'measure_frames',
  OPENED_LTUX_PA_UPSELL_COACHMARK: 'opened_ltux_pa_upsell_coachmark',
  PLAYBACK_INTERACTIONS: 'playback_interactions',
  PLAYED_PA_WELCOME_AUDIO: 'played_pa_welcome_audio',
  QUICK_MIX_EDIT: 'quick_mix_edit',
  QUICK_MIX_PLAY: 'quick_mix_play',
  REMOVE_AUTOPLAY_FEEDBACK: 'remove_autoplay_feedback',
  SEARCH_ACTION: 'search_action',
  SKIP_LIMIT: 'skip_limit',
  SOURCE_ADDED_TO_MY_MUSIC: 'Saved',
  SOURCE_CARD: 'source_card',
  SOURCE_CLICKED: 'Clicked',
  SOURCE_PLAY: 'Played',
  SOURCE_SHARED: 'Shared',
  SOURCE_STATION_STARTED: 'StationStarted',
  TIME_TO_MUSIC: 'time_to_music',
  TRACK_END: 'track_end',
  UPGRADE_CONFIRMATION: 'upgrade_confirmation',
  UPGRADE_PAGE: 'upgrade_page', // used to direct events to event_upgrade_page table
  USER_FACING_MESSAGES: 'user_facing_messages',
  VOICE_TRACK_METRIC: 'voice_track_metric_playlist',
  WEB_AD_BLOCKER: 'web_ad_blocker',
  WEB_BROWSE_SELECT: 'web_browse_select',
  WEB_CONCERT_NOTIFICATION_ACTION: 'web_concert_notification_action',
  WEB_REGISTRATION: 'web_registration', // used to direct events to event_web_registration table
  WEB_SORT: 'web_sort',
  WEB_STORE_SIZE: 'web_store_size',
} as const);

export const StatsStationKeys = Object.freeze({
  AMG_ARTIST: 'ar',
  AUTO_COMPLETE: 'mc',
  FACEBOOK_MIX_STATION: 'af',
  FACEBOOK_MUSIC_ID: 'mf',
  FACEBOOK_REFERRAL: 'fa',
  FACEBOOK_STATION_ID: 'sf',
  FACEBOOK_SUGGEST_SEARCH: 'fv',
  FRIEND_REFERRAL: 'fs',
  GIFTED: 'sg',
  MUSIC_ID: 'mi',
  QUICK_MIX: 'qm',
  RECOMMENDATION: 're',
  STATION_ID: 'sh',
  SUGGESTED_ARTIST: 'as',
  SUGGESTED_GENRE: 'gs',
  TWITTER_MUSIC_ID: 'mt',
  TWITTER_STATION_ID: 'sw',
} as const);

export const ViewModes = Object.freeze({
  ALBUM_ROLLOVER: 'album_rollover',
  ANONYMOUS: 'anonymous_listening',
  ANONYMOUS_LISTENING_TIMEOUT_REGISTRATION: 'anonymous_listening_timeout_registration',
  ATT: 'ATT',
  AUTO_COMPLETE: 'search_autocomplete_results',
  BROWSE: 'browse',
  BROWSE_ARTISTS_ON_THE_RISE: 'artists_on_the_rise_list',
  BROWSE_DECADES: 'decades_category_list',
  BROWSE_DECADES_CATEGORY: 'decades_stations_tile_list',
  BROWSE_FEATURED_PLAYLISTS: 'featured_playlists_stations_tile_list',
  BROWSE_GENRES: 'browse_genres_category_list',
  BROWSE_GENRES_CATEGORY: 'browse_genres_stations_tile_list',
  BROWSE_HOME: 'home',
  BROWSE_MOODS_AND_ACTIVITIES: 'moods_and_activities_category_tile_list',
  BROWSE_MOODS_AND_ACTIVITIES_CATEGORY: 'moods_and_activities_stations_tile_list',
  BROWSE_NEW_MUSIC: 'new_music_stations_tile_list',
  BROWSE_PODCASTS: 'podcast_categories_tile_list',
  BROWSE_PODCASTS_CATEGORY: 'podcast_category_stations_tile_list',
  BROWSE_RECOMMENDED_ARTISTS: 'recommended_artists_stations_tile_list',
  BROWSE_TOP_ARTISTS: 'top_artists_list',
  BROWSE_TOP_STATIONS: 'top_stations_list',
  FLEX_MARKETING_SUBSCRIPTION: 'flex_marketing_subscription',
  GENRES: 'genres',
  HOVER: 'hover',
  HOVER_MY_STATIONS_GRID: 'hover_my_stations_grid',
  LANDING_PAGE: 'landing_page',
  LOGIN_PAGE_SHOWN: 'login_page_shown',
  LOGIN_SUCCESS_PAGE_SHOWN: 'login_success_page_shown',
  MY_MUSIC: 'my_music',
  MY_MUSIC_ALBUMS: 'my_music_albums',
  MY_MUSIC_ALL: 'my_music_all',
  MY_MUSIC_ARTISTS: 'my_music_artists',
  MY_MUSIC_PLAYLISTS: 'my_music_playlists',
  MY_MUSIC_PODCASTS: 'my_music_podcasts',
  MY_MUSIC_SONGS: 'my_music_songs',
  MY_MUSIC_STATIONS: 'my_music_stations',
  MY_STATIONS_GRID: 'my_stations_grid',
  OFFER_URL_LOGIN: 'offer_url_login',
  OFFER_URL_REGISTRATION: 'offer_url_registration',
  PARTNER_URL_LOGIN: 'partner_url_login',
  PARTNER_URL_REGISTRATION: 'partner_url_registration',
  PASSWORD_HIDDEN: 'pwd_hidden',
  PASSWORD_SHOWN: 'pwd_shown',
  PLAYLIST_TRACKS: 'playlist_tracks',
  PROFILE: 'profile',
  RECENT_SORT: 'recent_sort',
  RECENTLY_PLAYED_CAROUSEL: 'recently_played_carousel',
  REDEEM_CODE: 'redeem_code',
  REGISTRATION_SUCCESS_PAGE_SHOWN: 'registration_success_page_shown',
  RESULT_ROLLOVER: 'result_rollover',
  SAMPLE: 'sample',
  SEARCH: 'search',
  SEARCH_PROMPT: 'search_prompt',
  SEARCH_RESULTS: 'search_results',
  SETTINGS: 'settings',
  STANDARD: 'standard',
  STATION_HINT: 'station_hint',
  SUBSCRIPTION: 'subscription',
  SWITCH_PLANS: 'switch_plans',
  THUMBS_UP: 'thumbs_up',
  UNDER_13: 'under_13',
  UPDATE_PAYMENT: 'update_payment',
  UPGRADE_FAMILY_PLAN: 'upgrade_family_plan',
  UPGRADE_MILITARY: 'upgrade_military',
  UPGRADE_PLUS: 'upgrade_plus',
  UPGRADE_PREMIUM: 'upgrade_premium',
  UPGRADE_STUDENT: 'upgrade_student',
  VIEW_PLANS: 'view_plans',
} as const);

export const DefaultFieldNames = Object.freeze({
  // AB experiment exposure logging event fields (using server names)
  // Ad fields
  ACTION: 'action',
  ACTION_SKU_FEATURE_CODE: 'action_sku_feature_code',
  AD_DISPLAY_TYPE: 'ad_display_type',
  AD_PLACEMENT: 'ad_placement',
  APP_VERSION: 'app_version',
  BROWSER: 'browser',
  BROWSER_HEIGHT: 'browser_height',
  BROWSER_ID: 'browser_id',
  BROWSER_VERSION: 'browser_version',
  BROWSER_WIDTH: 'browser_width',
  CLIENT_CONTEXT: 'client_context',
  CLIENT_TIMESTAMP: 'client_timestamp',
  CORRELATION_ID: 'correlation_id',
  CREATIVE_ID: 'creative_id',
  DATE_RECORDED: 'date_recorded',
  DAY: 'day',
  DEVICE_CODE: 'device_code',
  DEVICE_ID: 'device_id',
  DEVICE_OS: 'device_os',
  DEVICE_UUID: 'device_uuid',
  EVENT: 'event',
  EXPERIMENT_STATUS: 'experiment_status',
  IS_ON_DEMAND_USER: 'is_on_demand_user',
  KEY_AB_EXPERIMENT_ID: 'experiment_id',
  LINE_ID: 'line_id',
  LINK_CORRELATION_ID: 'link_correlation_id',
  LINK_PARTNER_ID: 'link_partner_id',
  LISTENER_ID: 'listener_id',
  LOG_DATE: 'log_date',
  MUSIC_PLAYING: 'music_playing',
  NOT_VISIBLE: 'backgrounded',
  PAGE_VIEW: 'page_view',
  REQUEST_PARAMS: 'request_params',
  RESPONSE_TREATMENT_ID: 'response_treatment_id',
  SITE_VERSION: 'site_version',
  SOURCE: 'source',
  SPECIAL_OFFER_CAMPAIGN_ID: 'campaign_id',
  SPECIAL_OFFER_CODE: 'promo_code',
  TIME: 'time',
  TUNER_VAR_FLAGS: 'tuner_var_flags',
  VENDOR_ID: 'vendor_id',
  VIEW_MODE: 'view_mode',
} as const);

export const DefaultValues = Object.freeze({
  STATS_VENDOR_ID: '100',
  STATS_VENDOR_ID_IOS: '20',
  STATS_VENDOR_ID_ANDROID: '42',
  STATS_VENDOR_ID_DESKTOP_APP: '184',
} as const);

export const TunerVarFieldNames = Object.freeze({
  ARTIST_COLLABORATIONS_ENABLED: 'artist_collaborations_enabled',
} as const);

export const SearchKeys = Object.freeze({
  CURRENT_SEARCH_SESSION_ID: 'currentSearchSessionId',
  CURRENT_SEARCH_SEQUENCE_NUMBER: 'currentSearchSequenceNumber',
} as const);

export const PerformanceKeys = Object.freeze({
  BUFFERING_MSEC: 'buffering_milliseconds',
  INITIAL_PLAYBACK: 'INITIAL_PLAYBACK',
  TTM_ACTION: 'action',
  TTM_AUDIO_TYPE: 'audio_type',
  TTM_MSEC: 'milliseconds_to_music',
  TTUI_MSEC: 'milliseconds_to_ui',
} as const);

export const TTMActions = Object.freeze({
  APP_LAUNCHED: 'app_launched',
  AUDIO_AD: 'audio_ad',
  NO_USER_ACTION: 'no_user_action',
  NON_AD_INTERRUPTION: 'non_ad_interruption',
  PLAYBACK_RESUMED: 'playback_resumed',
  SOURCE_CHANGED: 'source_changed',
  STILL_LISTENING: 'still_listening',
  TRACK_SKIPPED: 'track_skipped',
  TRACK_THUMBS_DOWN: 'track_thumbs_down',
  VIDEO_AD: 'video_ad',
} as const);

export const TTMAudioTypes = Object.freeze({
  MUSIC: 'music',
  VIDEO_AD: 'video_ad',
  NON_AD_INTERRUPTION: 'non_ad_interruption',
  AUDIO_AD: 'audio_ad',
} as const);

export const ChronosLifeCycleSubTypes = Object.freeze({
  TRACK_COMPLETE: 'track_complete',
  STATION_CHANGE: 'station_change',
  TRACK_SKIP: 'track_skip',
  TRACK_THUMBDOWN: 'track_thumbdown',
} as const);

export const TrackEndReasons = Object.freeze({
  BACK: 'back',
  COMPLETE: 'completed',
  DISCARDED: 'discarded',
  ERROR: 'error',
  EXPIRED: 'expired',
  REPLAY: 'replay',
  SKIP: 'skipped',
  STATION_CHANGED: 'station_changed',
  THUMBED_DOWN: 'thumbed_down',
} as const);

export const TrackEndSpinTypes = Object.freeze({
  RADIO: 'radio',
  REPLAY: 'replay',
} as const);

export const AUDIO_STATS_TEST_GROUP_NAME = 'WEB_REDESIGN_CLIENT' as const;

export const ConcertEventActionTypes = Object.freeze({
  SENT: 'sent',
  VIEWED: 'viewed',
} as const);

export const ConcertEventActionFromTypes = Object.freeze({
  HISTORY: 'history',
  NOW_PLAYING: 'now_playing',
  ARTIST_PAGE: 'artist_page',
} as const);

export const CoachmarkTypes = Object.freeze({
  AD_BLOCKER: 'ad_blocker',
  IN_VIDEO: 'in_video',
  ONBOARDING: 'onboarding',
  PA_CAP: 'pa_cap',
  PODCAST: 'podcast_wink',
  PREMIUM_ACCESS: 'premium_access',
  UPSELL: 'upsell',
} as const);

export const VoiceTrackMetricKeys = Object.freeze({
  AUTHOR_ID: 'author_id',
  MESSAGE_ID: 'message_id',
  METRIC_TYPE: 'metric_type',
  SOURCE_ID: 'source_id',
} as const);

export const VoiceTrackMetricTypes = Object.freeze({
  AUDIO_IMPRESSION: 'AUDIO_IMPRESSION',
  AUDIO_TILE_CLICK: 'AUDIO_TILE_CLICK',
  AUDIO_TILE_IMPRESSION: 'AUDIO_TILE_IMPRESSION',
  SKIP: 'SKIP',
} as const);

export const DiscoveryTunerActionKeys = Object.freeze({
  AUDIO_TOKEN: 'audio_token',
  CURRENT_MODE_ID: 'current_mode_id',
  INDEX: 'index',
  SELECTED_MODE_ID: 'selected_mode_id',
  SONG_ID: 'song_id',
  STATION_ID: 'station_id',
} as const);

export const PodcastViewModes = Object.freeze({
  PROGRAM_VIEW_EMPTY: 'program_view_empty',
  EPISODE_VIEW_EMPTY: 'episode_view_empty',
  PROGRAM_HAS_COLLECTED: 'program_has_collected',
  EPISODE_HAS_COLLECTED: 'episode_has_collected',
} as const);

export const SourceCardActions = Object.freeze({
  ADD_TO_PLAYLIST: 'add_to_playlist',
  CLOSE: 'close',
  COLLECT: 'collect',
  CREATE_STATION: 'create_station',
  DOWNLOAD: 'download',
  REMOVE_FROM_COLLECTION: 'remove_from_collection',
  SHARE: 'share',
  TIRED_TRACK: 'tired_track',
  WHY_SONG: 'why_song',
} as const);

export const UserMessageTypes = Object.freeze({
  INLINE: 'inline',
  TOAST: 'toast',
  MODAL: 'modal',
} as const);

export const mapMessageNameToId = Object.freeze({
  [D.Constants.Analytics.UserMessages.GENERIC_SUBSCRIPTION_FAILURE]: 1,
  [D.Constants.Analytics.UserMessages.INVALID_PANDORA_ID]: 7,
  [D.Constants.Analytics.UserMessages.ALREADY_VERIFIED]: 9,
  [D.Constants.Analytics.UserMessages.INVALID_AGE]: 10,
  [D.Constants.Analytics.UserMessages.PARTNER_UPGRADE]: 11,
  [D.Constants.Analytics.UserMessages.API_UNEXPECTED_ERROR]: 12,
  [D.Constants.Analytics.UserMessages.FETCH_PLAYLIST]: 13,
  [D.Constants.Analytics.UserMessages.GENERAL_SERVER_ERROR]: 14,
  [D.Constants.Analytics.UserMessages.ACQUIRE_PRODUCT_FAILED]: 15,
  [D.Constants.Analytics.UserMessages.GENERIC_COMPONENT_ERROR]: 16,
  [D.Constants.Analytics.UserMessages.INELIGIBLE_PARTNER_UPGRADE]: 17,
  [D.Constants.Analytics.UserMessages.FP_AGE_REQUIREMENT]: 18,
  [D.Constants.Analytics.UserMessages.ALREADY_SUBSCRIBED]: 19,
  [D.Constants.Analytics.UserMessages.FP_INELIGIBLE_API_ERROR]: 20,
  [D.Constants.Analytics.UserMessages.INVALID_CODE]: 21,
  [D.Constants.Analytics.UserMessages.PLAYBACK_FAILURE]: 22,
  [D.Constants.Analytics.UserMessages.START_STATION_ERROR]: 23,
  [D.Constants.Analytics.UserMessages.LISTENER_NOT_AUTHORIZED]: 25,
  [D.Constants.Analytics.UserMessages.BANDWIDTH_INSUFFICIENT]: 26,
} as const);

/* max number of chars per stats call */
export const MAX_NUM_CHARS_IN_BATCH = 10000 as const;

export const mapActionTypeToEventSubType = Object.freeze({
  [D.Constants.AudioPlayer.AudioPlayerActions.UPDATE]: ChronosLifeCycleSubTypes.TRACK_COMPLETE,
  [D.Constants.Playback.ActionTypes.SET_OR_CREATE_STATION]: ChronosLifeCycleSubTypes.STATION_CHANGE,
  [D.Constants.Playback.ActionTypes.TRACK_THUMBDOWN]: ChronosLifeCycleSubTypes.TRACK_THUMBDOWN,
  [D.Constants.Playback.ActionTypes.TIRE_OF_TRACK]: ChronosLifeCycleSubTypes.TRACK_SKIP,
  [D.Constants.UseCases.ActionTypes.SKIP_TRACK_FORWARD]: ChronosLifeCycleSubTypes.TRACK_SKIP,
} as const);

export const mapActionTypeToStatsActionType = Object.freeze({
  [D.Constants.AudioPlayer.AudioPlayerActions.AUDIO_PLAY_TRACK]: StatsActions.PLAY.toLowerCase(),
  [D.Constants.AudioPlayer.AudioPlayerActions.PAUSE]: StatsActions.PAUSE.toLowerCase(),
  [D.Constants.SourceCard.SourceCardConstants.DELETEPLAYLIST]: StatsActions.DELETE_PLAYLIST,
  [D.Constants.SourceCard.SourceCardConstants.ADDTOPLAYLIST]: StatsActions.ADD_TO_PLAYLIST,
  [D.Constants.SourceCard.SourceCardConstants.REMOVEFROMPLAYLIST]:
    StatsActions.REMOVE_FROM_PLAYLIST,
  [D.Constants.SourceCard.SourceCardConstants.NEWPLAYLIST]: StatsActions.NEW_PLAYLIST,
  [D.Constants.SourceCard.SourceCardConstants.ADDTOCOLLECTION]: StatsActions.COLLECT,
  [D.Constants.SourceCard.SourceCardConstants.REMOVEFROMCOLLECTION]: StatsActions.UNCOLLECT,
  [D.Constants.SourceCard.SourceCardConstants.STARTSTATIONTRACK]: StatsActions.START_STATION_TRACK,
  [D.Constants.SourceCard.SourceCardConstants.STARTSTATIONARTIST]:
    StatsActions.START_STATION_ARTIST,
  [D.Constants.SourceCard.SourceCardConstants.SHARE]: StatsActions.SHARE,
  [D.Constants.SourceCard.SourceCardConstants.DELETESTATION]: StatsActions.DELETE_STATION,
  [D.Constants.SourceCard.SourceCardConstants.WHYSELECTED]: StatsActions.WHY_SONG,
  [D.Constants.SourceCard.SourceCardConstants.TIRED]: StatsActions.TIRED_TRACK,
  [D.Constants.SourceCard.SourceCardTriggerTypes.STATION_TRIGGER]: StatsActions.START_STATION,
  [D.Constants.SourceCard.SourceCardTriggerTypes.ELLIPSIS_TRIGGER]: StatsActions.OVERFLOW,
  [D.Constants.Rights.RightsConstants.INSUFFICIENT_RIGHTS_TOAST]:
    StatsActions.INSUFFICIENT_RIGHTS_TOAST,
  [D.Constants.PlaylistT3.ActionTypes.SET_DETAILS]: StatsActions.EDIT,
  [D.Constants.PlaylistT3.ActionTypes.GET_PLAYLIST_RECOMMENDATIONS]: StatsActions.SEARCH_SONG,
  [D.Constants.PlaylistT3.ActionTypes.GET_PLAYLIST_AUTOFILL_TRACKS]: StatsActions.ADD_SONGS,
  [D.Constants.StationDetails.CollectedStationComponentEventTypes.AAM_TOGGLE_ON]:
    StatsActions.AAM_TOGGLE_ON,
  [D.Constants.StationDetails.CollectedStationComponentEventTypes.AAM_TOGGLE_OFF]:
    StatsActions.AAM_TOGGLE_OFF,
  [D.Constants.StationDetails.CollectedStationComponentEventTypes.EDIT_DESCRIPTION]:
    StatsActions.EDIT_DESCRIPTION,
  [D.Constants.StationDetails.CollectedStationComponentEventTypes.ADD_VARIETY]:
    StatsActions.ADD_VARIETY,
  [D.Constants.StationDetails.CollectedStationComponentEventTypes.SEE_ALL]: StatsActions.SEE_ALL,
  [D.Constants.StationDetails.CollectedStationComponentEventTypes.SKIP_TO_THUMBS]:
    StatsActions.SKIP_TO_THUMBS,
} as const);

export const mapFilterTypesToViewModes = Object.freeze({
  [D.Constants.FilterNav.FilterNavTypes.ALL]: ViewModes.MY_MUSIC_ALL,
  [D.Constants.FilterNav.FilterNavTypes.ALBUMS]: ViewModes.MY_MUSIC_ALBUMS,
  [D.Constants.FilterNav.FilterNavTypes.ARTISTS]: ViewModes.MY_MUSIC_ARTISTS,
  [D.Constants.FilterNav.FilterNavTypes.SONGS]: ViewModes.MY_MUSIC_SONGS,
  [D.Constants.FilterNav.FilterNavTypes.STATIONS]: ViewModes.MY_MUSIC_STATIONS,
  [D.Constants.FilterNav.FilterNavTypes.PLAYLISTS]: ViewModes.MY_MUSIC_PLAYLISTS,
} as const);

export const MapSourceTypeToSourceCardType = Object.freeze({
  [D.Constants.SourceTypes.PandoraTypes.SONG]: 'track',
  [D.Constants.SourceTypes.PandoraTypes.ALBUM]: 'album',
  [D.Constants.SourceTypes.PandoraTypes.PLAYLIST]: 'playlist',
  [D.Constants.SourceTypes.PandoraTypes.ARTIST]: 'artist',
  [D.Constants.SourceTypes.PandoraTypes.STATION]: 'station',
  [D.Constants.SourceTypes.PandoraTypes.EPISODE]: 'podcast_episode',
  [D.Constants.SourceTypes.PandoraTypes.PODCAST]: 'podcast_series',
} as const);

export const MapNavigationTypeToStatsActionType = Object.freeze({
  [D.Constants.Discovery.NavigationTypes.BACKSTAGE]: StatsActions.ROUTE_TO_BACKSTAGE,
  [D.Constants.Discovery.NavigationTypes.DIRECTORY]: StatsActions.DIRECTORY,
  [D.Constants.Discovery.NavigationTypes.VIEW_ALL]: StatsActions.VIEW_ALL,
} as const);
